import Decimal from "decimal.js"
import PropTypes from "prop-types"
import React, { useContext, useEffect, useMemo, useRef, useState } from "react"
import { useForm } from "react-hook-form"
import { useMutation, useQuery } from "react-query"
import { useMarina } from "src/contexts/MarinaContext"
import MaxPaymentAmountDisplay from "src/main/Billing/MaxPaymentAmountDisplay"
import PaymentMethodDropdown from "src/main/Billing/PaymentMethodDropdown"
import RefundPaymentMethodDropdown, {
  formatPaymentMethods,
} from "src/main/Billing/RefundPaymentMethodDropdown"
import { SharedBillingContext } from "src/main/Billing/SharedBillingContext"
import { useGetRefundablePaymentMethods } from "src/main/Billing/hooks"

import AlertBanner from "src/components/AlertBanner"
import Button from "src/components/Button"
import Form from "src/components/Form"
import Modal from "src/components/Modal"
import ReloadableWidget from "src/components/ReloadableWidget"

import { createInvoiceTxn, getSurchargeCopy } from "src/api/Billing/Payments"

import { formatCurrencyFromMinorUnits } from "src/utils/UnitConversion"
import { capitalize } from "src/utils/string_helpers"

import {
  getRequestPaymentMethodId,
  paymentMethodIsCreditCard,
} from "../helpers"
import CardReaderProcessing from "./CardReaderProcessing"
import { CARD_READER_PROCESSED, CARD_READER_SELECTED } from "./amplitude_events"
import {
  CARD_READER_PAYMENT_METHOD,
  OFFLINE_PAYMENT_METHODS,
  getTxnAttributes,
} from "./helpers"
import { useUpdateInvoice } from "./hooks"
import useCardReaderPayment from "./useCardReaderPayment"

const SURCHARGE_ELIGIBILITY_CHECK_ERROR =
  "We encountered an error determining if a surcharge would apply to this payment upon settle. Please contact Dockwa support at mayday@dockwa.com for assistance."
const GENERIC_SERVER_ERROR = {
  type: "server",
  message:
    "Something went wrong settling this payment. Please try again or contact support.",
}
const CARD_READER_COPY = {
  cancelTooLate:
    "The customer has already presented their card. Please wait for the payment to complete.",
  connecting: "Connecting to the card reader…",
  connectingToReader: "Connecting to %{name}…",
  declined: "The card reader payment did not go through. Please try again.",
  presentCard: "Ask the customer to present their card on the reader.",
  stillProcessing:
    "This payment is still processing. You can close this window and check back shortly.",
  unavailable:
    "Card reader unavailable. Please make sure it's online and try again.",
  waiting: "Waiting for payment on the card reader",
  waitingOnReader: "Waiting for payment on %{name}",
}

const renderManualPaymentMetadataFields = (paymentType, register) => {
  switch (paymentType) {
    case "check":
      return (
        <>
          <Form.Label htmlFor="check-number">Check number</Form.Label>
          <Form.TextField
            id="check-number"
            {...register("txn.offline_payment_attributes.check_number")}
          />
        </>
      )
    case "manual_ach":
      return (
        <>
          <Form.Label htmlFor="ach-confirmation">ACH confirmation</Form.Label>
          <Form.TextField
            id="ach-confirmation"
            {...register("txn.offline_payment_attributes.ach_confirmation")}
          />
        </>
      )
    case "external_txn":
      return (
        <>
          <Form.Label htmlFor="note">Note</Form.Label>
          <Form.TextField
            id="note"
            {...register("txn.offline_payment_attributes.notes")}
          />
        </>
      )
    default:
      return null
  }
}

const SettlePaymentModal = ({
  payment,
  onPaymentSettle,
  isOpen,
  onClose,
  afterLeave,
  paymentMethods,
}) => {
  const { presentmentCurrency, marinaSlug, currencySymbol } = useMarina()
  const partialSettlementCheckboxRef = useRef()
  const [isPartialSettlement, setIsPartialSettlement] = useState(false)
  const [isCreditCard, setIsCreditCard] = useState(false)
  const [showSurchargeNotice, setShowSurchargeNotice] = useState(false)
  const [selectedCardReaderId, setSelectedCardReaderId] = useState(null)
  const {
    contractSurchargeEligible,
    cardReaders = [],
    enableBluetoothCardReader = false,
  } = useContext(SharedBillingContext) || {}

  const {
    register,
    control,
    watch,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
    setError,
    clearErrors,
  } = useForm({
    shouldUnregister: true,
    defaultValues: {
      txn: {
        offline_payment_attributes: {
          payment_type: "cash",
        },
      },
    },
    mode: "all",
  })

  const [paymentType, partialPayment, paymentMethodId] = watch([
    "txn.offline_payment_attributes.payment_type",
    "partialPayment",
    "invoice.payment_method_id",
  ])

  const resetForm = () => {
    reset()
    setIsPartialSettlement(false)
    cardReaderPayment.reset()
    setSelectedCardReaderId(null)
  }

  const onResetModal = () => {
    if (afterLeave) {
      afterLeave()
    }
    resetForm()
  }

  const invoiceId = payment?.id
  const balanceInCents = payment?.balance ?? 0
  const absBalanceInCents = new Decimal(balanceInCents).abs().toNumber()
  const formattedAbsBalance = formatCurrencyFromMinorUnits({
    amount: absBalanceInCents,
    currency: presentmentCurrency,
  })
  const settleType = new Decimal(balanceInCents).lt(0) ? "refund" : "payment"
  const isRefund = settleType === "refund"
  const allowsPartialRefund = Boolean(payment?.allows_partial_refund)
  const billingContainerId = payment?.billing_container_id
  const { cards: paymentCards } = paymentMethods
  const partialPaymentInCents = new Decimal(partialPayment || 0)
    .mul(100)
    .toNumber()
  const existingPaymentMethodId = payment?.payment_method?.id || "manual"
  const existingPaymentMethod = isRefund
    ? String(existingPaymentMethodId)
    : existingPaymentMethodId

  const { mutate, isLoading } = useMutation({
    queryKey: ["invoiceTxn", invoiceId],
    mutationFn: (data) => createInvoiceTxn({ invoiceId, marinaSlug, data }),
    onSuccess: () => {
      onPaymentSettle()
    },
    onError: () => {
      setError("root", GENERIC_SERVER_ERROR)
    },
  })

  const isCardPresentSelected = paymentMethodId === CARD_READER_PAYMENT_METHOD
  const hasCardReaders = cardReaders.length > 0

  const cardReaderPayment = useCardReaderPayment({
    invoiceId,
    marinaSlug,
    cardReaderId: selectedCardReaderId,
    enableBluetoothCardReader,
    isSelected: isCardPresentSelected,
    events: {
      selected: CARD_READER_SELECTED,
      processed: CARD_READER_PROCESSED,
    },
    submit: (data) => createInvoiceTxn({ invoiceId, marinaSlug, data }),
    // POST /invoices/:id/txns returns the created txns.
    readReaderUnconfirmed: (response) =>
      response?.[0]?.stripe_payment?.reader_unconfirmed,
    onSettled: onPaymentSettle,
    onError: (message) => setError("root", { type: "server", message }),
    genericErrorMessage: GENERIC_SERVER_ERROR.message,
    copy: CARD_READER_COPY,
  })
  const { isCollecting, isTimedOut, isProcessingView } = cardReaderPayment

  const {
    isFetching: isLoadingSurchargeCopy,
    isError: isErrorSurchargeCopy,
    data: { surcharge_copy: surchargeCopy },
  } = useQuery(
    ["invoiceSurchargeCopy", payment?.id],
    () => getSurchargeCopy({ invoiceId: payment?.id, marinaSlug }),
    {
      initialData: { surcharge_copy: null },
      enabled: !!(payment?.id && !isRefund),
      refetchOnWindowFocus: false,
      retry: false,
    }
  )

  // Fetching Payment Methods
  const {
    data: paymentMethodResponse,
    isFetching: isLoadingRefundablePaymentMethods,
  } = useGetRefundablePaymentMethods({
    billingContainerId,
    isSettling: true, // include all methods, ignoring scheduled refunds
    options: {
      onSuccess: ({
        refundablePaymentMethods: data,
        isMultiplePaymentRequired,
      }) => {
        if (
          allowsPartialRefund &&
          isMultiplePaymentRequired &&
          !isPartialSettlement
        ) {
          setIsPartialSettlement(true)
        }

        const paymentMethod = data.find(
          (pm) => pm.payment_method_id === String(paymentMethodId)
        )

        // If the current payment method is not refundable, select the first refundable method
        const refundablePaymentMethodId = paymentMethod
          ? paymentMethod.payment_method_id
          : data[0]?.payment_method_id

        if (String(refundablePaymentMethodId) !== String(paymentMethodId)) {
          setValue("invoice.payment_method_id", refundablePaymentMethodId)
        }
      },
      select: (data) => ({
        refundablePaymentMethods: formatPaymentMethods(data),
        isMultiplePaymentRequired: data.every(
          (paymentMethod) => paymentMethod.refundable_amount < absBalanceInCents
        ),
      }),
      onError: (error) => {
        console.log("queryRefundablePaymentMethods error", { error })
      },
      enabled: isRefund && isOpen,
    },
  })

  const { refundablePaymentMethods, isMultiplePaymentRequired } =
    paymentMethodResponse ?? {
      refundablePaymentMethods: [],
      isMultiplePaymentRequired: null,
    }

  const refundableAmount = useMemo(() => {
    if (!isRefund || refundablePaymentMethods.length === 0) {
      return 0
    }

    const refundablePaymentMethod = refundablePaymentMethods.find(
      (paymentMethod) => paymentMethod.payment_method_id === paymentMethodId
    )

    return refundablePaymentMethod
      ? refundablePaymentMethod.refundable_amount
      : 0
  }, [isRefund, refundablePaymentMethods, paymentMethodId])

  const formattedRefundableAmount = formatCurrencyFromMinorUnits({
    amount: refundableAmount,
    currency: presentmentCurrency,
  })

  // Each opening, and each switch to another invoice while open, starts from a
  // clean form seeded with that invoice's own method. The modal can reopen
  // before its close finishes, so afterLeave's reset is not enough. It must not
  // re-run on other prop changes: callers may pass a new payment object on
  // every render, and re-seeding would discard the method the user picked.
  useEffect(() => {
    if (!isOpen) {
      return
    }
    resetForm()
    setValue("invoice.payment_method_id", existingPaymentMethod)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, invoiceId])

  useEffect(() => {
    const isCreditCard = paymentMethodIsCreditCard(
      "id",
      paymentMethodId,
      paymentCards
    )

    setIsCreditCard(isCreditCard)
  }, [paymentCards, paymentMethodId])

  useEffect(() => {
    setShowSurchargeNotice(surchargeCopy && isCreditCard)
  }, [surchargeCopy, isCreditCard])

  const createInvoiceTransaction = (data, currentPayment) => {
    const requestData = {
      txn: getTxnAttributes({ data, payment: currentPayment }),
    }
    mutate(requestData)
  }

  const submitCardPresentPayment = (data) => {
    clearErrors("root")
    cardReaderPayment.start({
      txn: getTxnAttributes({
        data,
        payment,
        cardReaderId: selectedCardReaderId,
      }),
    })
  }

  // invoked for refund creations if the payment method has changed
  // to first update the invoice before creating the transaction
  const { submit: updateInvoice, isLoading: isLoadingInvoice } =
    useUpdateInvoice({
      paymentId: payment?.id,
      onSuccess: (payment) =>
        handleSubmit((data) => createInvoiceTransaction(data, payment))(),
      onError: () => {
        setError("root", GENERIC_SERVER_ERROR)
      },
    })

  const onSubmit = (data) => {
    if (isCardPresentSelected) {
      submitCardPresentPayment(data)
      return
    }
    if (String(existingPaymentMethod) !== String(paymentMethodId)) {
      if (isRefund) {
        updateInvoice(data)
      } else {
        createInvoiceTransaction(data, {
          ...payment,
          payment_method: {
            ...(payment?.payment_method ? payment?.payment_method : {}),
            id: getRequestPaymentMethodId(paymentMethodId),
          },
        })
      }
    } else {
      createInvoiceTransaction(data, payment)
    }
  }

  const renderSurchargeNotice = () => {
    if (showSurchargeNotice) {
      return (
        <div className="mb-5">
          <AlertBanner type="info">{surchargeCopy}</AlertBanner>
        </div>
      )
    }
  }

  const onModalClose = () => {
    // Don't let staff dismiss the modal mid-collection — the reader is live.
    if (isCollecting) {
      return
    }
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onModalClose}
      maxSize="medium"
      afterLeave={onResetModal}
    >
      <Modal.Header title="Settle now" />
      <Modal.Body>
        {isProcessingView ? (
          <CardReaderProcessing
            isCollecting={isCollecting}
            isReaderUnconfirmed={cardReaderPayment.isReaderUnconfirmed}
            cardReaders={cardReaders}
            cardReaderId={selectedCardReaderId}
            errorMessage={errors.root?.message}
            copy={CARD_READER_COPY}
          />
        ) : null}
        {/* Hidden rather than unmounted while the reader is processing: with
            shouldUnregister, unmounting the fields discards their values, and a
            failed or canceled reader payment comes back here to retry. */}
        <div hidden={isProcessingView}>
          <ReloadableWidget isLoading={isLoadingSurchargeCopy} isError={false}>
            {isRefund && allowsPartialRefund && isMultiplePaymentRequired && (
              <div className="mb-5">
                <AlertBanner type="info">
                  There is a total of
                  <strong>{` ${formattedAbsBalance} `}</strong>
                  owed to the customer. You can refund up to
                  <strong>{` ${formattedRefundableAmount} `}</strong>
                  on this payment method. Please settle the partial amount and
                  refund the rest separately
                </AlertBanner>
              </div>
            )}
            <div className="mb-5 w-3/4">
              {isRefund ? (
                <RefundPaymentMethodDropdown
                  id="refund-payment-method"
                  name="invoice.payment_method_id"
                  control={control}
                  isLoading={isLoadingRefundablePaymentMethods}
                  paymentMethods={refundablePaymentMethods}
                  registerOptions={{
                    validate: (val) =>
                      val ? null : "Refund method is required.",
                  }}
                  errors={errors?.invoice?.payment_method_id}
                />
              ) : (
                <PaymentMethodDropdown
                  name="invoice.payment_method_id"
                  onlineMethods={paymentCards}
                  control={control}
                  includeExpirationDates
                  disabled={isRefund}
                  existingPaymentMethod={payment?.payment_method}
                  showCardReader={hasCardReaders}
                />
              )}
            </div>
            {isCardPresentSelected && (
              <div className="mb-5 w-3/4">
                <Form.Label htmlFor="card-reader">Card reader</Form.Label>
                <Form.Select
                  id="card-reader"
                  value={selectedCardReaderId ?? ""}
                  onChange={(event) =>
                    setSelectedCardReaderId(event.target.value)
                  }
                >
                  <option value="" disabled>
                    Select a card reader
                  </option>
                  {cardReaders.map((cardReader) => (
                    <option key={cardReader.id} value={cardReader.id}>
                      {cardReader.displayName}
                    </option>
                  ))}
                </Form.Select>
              </div>
            )}
            {renderSurchargeNotice()}
            {isErrorSurchargeCopy &&
            contractSurchargeEligible &&
            isCreditCard ? (
              <div className="my-5">
                <Form.Error>{SURCHARGE_ELIGIBILITY_CHECK_ERROR}</Form.Error>
              </div>
            ) : null}
            {paymentMethodId === "manual" && (
              <div className="mb-5 flex w-full flex-row gap-4">
                <div className="flex w-1/2 flex-col">
                  <Form.Label htmlFor="manual-payment-type">
                    Manual payment type
                  </Form.Label>
                  <Form.Select
                    id="manual-payment-type"
                    {...register("txn.offline_payment_attributes.payment_type")}
                  >
                    {Object.entries(OFFLINE_PAYMENT_METHODS).map(
                      ([value, displayName]) => (
                        <option key={value} value={value}>
                          {displayName}
                        </option>
                      )
                    )}
                  </Form.Select>
                </div>
                <div className="flex w-1/2 flex-col">
                  {renderManualPaymentMetadataFields(paymentType, register)}
                </div>
              </div>
            )}
            {!isPartialSettlement ? (
              <div className="flex flex-row gap-4">
                <div className="mb-5 flex w-1/2 flex-col">
                  <Form.Label htmlFor="full-payment-amount">
                    {capitalize(settleType)} amount
                  </Form.Label>
                  <Form.IconTextField
                    id="full-payment-amount"
                    position="left"
                    icon={currencySymbol}
                    type="number"
                    disabled
                    value={new Decimal(
                      isRefund ? balanceInCents : absBalanceInCents
                    )
                      .div(100)
                      .toFixed(2)}
                  />
                </div>
                <div className="mb-5 w-1/2"></div>
              </div>
            ) : (
              <div className="mb-5 flex flex-row gap-4">
                <div className="flex w-full flex-col">
                  <Form.Label htmlFor="partial-payment-amount">
                    {isRefund
                      ? "Partial refund amount"
                      : "Partial payment amount"}
                  </Form.Label>
                  <div className="-mt-2 mb-2">
                    <MaxPaymentAmountDisplay
                      amount={isRefund ? refundableAmount : absBalanceInCents}
                      displayZero
                    />
                  </div>
                  <Form.IconTextField
                    id="partial-payment-amount"
                    position="left"
                    icon={currencySymbol}
                    type="number"
                    {...register("partialPayment", {
                      validate: (val) => {
                        const amount = new Decimal(val || 0).mul(100).toNumber()
                        if (!amount || amount < 0) {
                          return "Partial payment amount must be greater than 0."
                        }
                        if (isRefund && amount > refundableAmount) {
                          return `Partial ${settleType} cannot exceed ${settleType} amount of ${formattedRefundableAmount}.`
                        }
                        if (amount > absBalanceInCents) {
                          return `Partial payment cannot exceed ${settleType} amount of ${formattedAbsBalance}.`
                        }
                      },
                    })}
                    hasErrors={!!errors.partialPayment}
                  />
                  {errors.partialPayment && (
                    <Form.Error>{errors.partialPayment.message}</Form.Error>
                  )}
                </div>
                <div className="flex w-full flex-col">
                  <Form.Label htmlFor="remainder-payment-amount">
                    Remaining amount
                  </Form.Label>
                  <div className="text-muted -mt-2 mb-2">
                    (scheduled separately)
                  </div>
                  <Form.IconTextField
                    id="remainder-payment-amount"
                    position="left"
                    icon={currencySymbol}
                    type="number"
                    disabled
                    value={
                      errors.partialPayment
                        ? ""
                        : new Decimal(absBalanceInCents)
                            .minus(partialPaymentInCents)
                            .div(100)
                            .toFixed(2)
                    }
                  />
                </div>
              </div>
            )}
            {(allowsPartialRefund || !isRefund) && (
              <div className="mb-5">
                <Form.Checkbox
                  name="partial-settlement"
                  ref={partialSettlementCheckboxRef}
                  onChange={() => {
                    setIsPartialSettlement(!isPartialSettlement)
                  }}
                  checked={isPartialSettlement}
                  disabled={isMultiplePaymentRequired}
                  compact
                  label="Settle partial amount"
                />
              </div>
            )}
          </ReloadableWidget>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="flex flex-col justify-end gap-4">
          <div className="flex justify-end">
            {isCollecting ? (
              <Button
                variant="tertiary"
                onClick={cardReaderPayment.cancel}
                isLoading={cardReaderPayment.isCanceling}
              >
                Cancel payment
              </Button>
            ) : isTimedOut ? (
              <Button variant="primary" onClick={onClose}>
                Close
              </Button>
            ) : (
              <>
                <div className="mr-5">
                  <Button variant="tertiary" onClick={onClose}>
                    Cancel
                  </Button>
                </div>
                <Button
                  type="submit"
                  variant="primary"
                  onClick={handleSubmit(onSubmit)}
                  disabled={
                    (!isRefund && !paymentMethodId) ||
                    (isCardPresentSelected && !selectedCardReaderId)
                  }
                  isLoading={
                    isLoading ||
                    cardReaderPayment.isStarting ||
                    isLoadingInvoice ||
                    isLoadingRefundablePaymentMethods ||
                    isLoadingSurchargeCopy
                  }
                >
                  {isPartialSettlement ? "Settle partial amount" : "Settle now"}
                </Button>
              </>
            )}
          </div>
          {!isProcessingView && errors.root ? (
            <Form.Error>{errors.root.message}</Form.Error>
          ) : null}
        </div>
      </Modal.Footer>
    </Modal>
  )
}

SettlePaymentModal.propTypes = {
  payment: PropTypes.shape({
    id: PropTypes.string.isRequired,
    billing_container_id: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),
    allows_partial_refund: PropTypes.bool,
    balance: PropTypes.number.isRequired,
    payment_method: PropTypes.object,
  }),
  onPaymentSettle: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  afterLeave: PropTypes.func,
  paymentMethods: PropTypes.shape({
    cards: PropTypes.array.isRequired,
  }),
}

export default SettlePaymentModal
